import { LocalStorageService } from '@/utils/storage';
import config from '@/config';
import { Commit } from 'vuex';
import {
  ADD_TAG_ONLY,
  ADD_CACHE_ONLY,
  ADD_TAG_AND_CACHE,
  SET_VISITED_VIEWS,
  DEL_CACHE_ONLY,
  DEL_TAG_AND_CACHE,
  DEL_TAG_ONLY,
  DEL_OTHER_TAG_AND_CACHE,
  DEL_LEFT_TAG_AND_CACHE,
  DEL_RIGHT_TAG_AND_CACHE,
  DEL_ALL_TAG_AND_CACHE,
} from '../actionTypes';

const cName = 'name';
const max = config.multipleNav.max; // 最多可以缓存多少组件实例。
const clientId = config.multipleNav.clientId;

type State = {
  visitedViews: TypeObject,
  cachedViews: string[]
}

const storeState: State = {
  visitedViews: LocalStorageService.get('_visited_views') || {}, // 多导航标签
  cachedViews: [], // 缓存的页签 include 值
};

const actions = {
  [ADD_TAG_AND_CACHE]({ commit }: { commit: Commit }, route: TypeObject): void {
    commit(ADD_TAG_ONLY, route);
    commit(ADD_CACHE_ONLY, route);
  },
  [DEL_TAG_AND_CACHE]({ commit }: { commit: Commit }, route: TypeObject): void {
    commit(DEL_TAG_ONLY, route);
    commit(DEL_CACHE_ONLY, route);
  },
};

const mutations = {
  [ADD_TAG_ONLY](state: State, route: TypeObject): void {
    const fixedViews = config.multipleNav.fixedRoute.map((i) => i.fullPath);
    const visitedViewsTemp = state.visitedViews[clientId] || [];
    const index = visitedViewsTemp.findIndex((vRoute: TypeObject) => vRoute[cName] === route[cName]);
    const routeInfo = {
      fullPath: route.fullPath,
      hash: route.hash,
      meta: route.meta,
      name: route.name,
      params: route.params,
      path: route.path,
      query: route.query,
    };
    if (index > -1) {
      visitedViewsTemp[index] = routeInfo;
      state.visitedViews[clientId] = visitedViewsTemp;
      LocalStorageService.set('_visited_views', state.visitedViews);
      return;
    }
    if (!fixedViews.includes(routeInfo.fullPath)) {
      visitedViewsTemp.push(routeInfo);
    }
    state.visitedViews[clientId] = visitedViewsTemp;
    LocalStorageService.set('_visited_views', state.visitedViews);
  },
  [ADD_CACHE_ONLY](state: State, route: TypeObject): void {
    const { noCache, iframe } = route.meta || {};
    if (noCache || iframe || (!route[cName])) {
      return;
    }

    const fixedViewsCached = config.multipleNav.fixedViews;
    const cachedKey = route[cName];
    if (state.cachedViews.includes(cachedKey)) { // 已缓存先删除缓存的key
      const index = state.cachedViews.indexOf(cachedKey);
      index > -1 && state.cachedViews.splice(index, 1);
    }
    if (!(fixedViewsCached as any[]).includes(cachedKey)) {
      state.cachedViews.push(cachedKey);
    }
    if (state.cachedViews.length > max) {
      // 超过缓存限制，删除最近不常操作的缓存
      state.cachedViews = state.cachedViews.slice(-max);
    }
  },

  [SET_VISITED_VIEWS](state: State, visitedViews: TypeObject): void {
    state.visitedViews[clientId] = visitedViews;
    LocalStorageService.set('_visited_views', state.visitedViews);
  },

  [DEL_CACHE_ONLY](state: State, route: TypeObject): void {
    const cachedKey = route[cName];
    const index = state.cachedViews.indexOf(cachedKey);
    index > -1 && state.cachedViews.splice(index, 1);
  },

  [DEL_TAG_ONLY](state: State, route: TypeObject): void {
    const visitedViewsTemp = state.visitedViews[clientId] || [];
    const index = visitedViewsTemp.findIndex((nav: TypeObject) => nav.fullPath === route.fullPath);
    index > -1 && visitedViewsTemp.splice(index, 1);

    state.visitedViews[clientId] = visitedViewsTemp;
    LocalStorageService.set('_visited_views', state.visitedViews);
  },

  [DEL_OTHER_TAG_AND_CACHE](state: State, route: TypeObject): void {
    const visitedViewsTemp = state.visitedViews[clientId] || [];
    const curIndex = visitedViewsTemp.findIndex((nav: TypeObject) => nav.fullPath === route.fullPath);
    const visitedView = visitedViewsTemp[curIndex];
    const key = route[cName];
    state.visitedViews[clientId] = visitedView ? [visitedView] : [];
    state.cachedViews = key ? [key] : [];

    LocalStorageService.set('_visited_views', state.visitedViews);
  },

  [DEL_LEFT_TAG_AND_CACHE](state: State, route: TypeObject): void {
    const visitedViewsTemp = state.visitedViews[clientId] || [];
    const curIndex = visitedViewsTemp.findIndex((nav: TypeObject) => nav.fullPath === route.fullPath);
    const visitedViewsRight = visitedViewsTemp.slice(curIndex);
    state.visitedViews[clientId] = visitedViewsRight;

    const rightViewsKeys = visitedViewsRight.map((nav: TypeObject) => nav.name || '');
    state.cachedViews = state.cachedViews.filter((key: string) => rightViewsKeys.includes(key));

    LocalStorageService.set('_visited_views', state.visitedViews);
  },

  [DEL_RIGHT_TAG_AND_CACHE](state: State, route: TypeObject): void {
    const visitedViewsTemp = state.visitedViews[clientId] || [];
    const curIndex = visitedViewsTemp.findIndex((nav: TypeObject) => nav.fullPath === route.fullPath);
    const visitedViewsLeft = visitedViewsTemp.slice(0, curIndex + 1);
    state.visitedViews[clientId] = visitedViewsLeft;

    const leftViewsKeys = visitedViewsLeft.map((nav: TypeObject) => nav.name || '');
    state.cachedViews = state.cachedViews.filter((key: string) => leftViewsKeys.includes(key));

    LocalStorageService.set('_visited_views', state.visitedViews);
  },

  [DEL_ALL_TAG_AND_CACHE](state: State): void {
    state.visitedViews[clientId] = [];
    state.cachedViews = [];
    LocalStorageService.set('_visited_views', state.visitedViews);
  },
};

export default {
  namespaced: true,
  state: storeState,
  actions,
  mutations,
};
